import { authenticatedApi } from '../api';
import { IListResult } from '../../models/IListResult';

export interface MessageSender {
  id: string;
  name: string;
  email: string;
  type: 'agent' | 'beneficiary';
}

export interface MessageContent {
  text: string;
  html?: string;
  subject?: string;
  attachmentsIds?: number[];
  metadata?: Record<string, any>;
  requiresAttention?: boolean; // Pour les messages internes uniquement
}

export interface Message {
  id: number;
  content: MessageContent;
  user?: {
    uuid?: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  fromUserUUID: string | null;
  senderType: 'gestionnaire' | 'garant' | 'beneficiary' | 'system' | 'repairer';
  dateCreation: string;
  channelKey: string;
}

// Type étendu pour l'UI avec statut d'envoi
export interface MessageWithStatus extends Message {
  sending?: boolean;
  error?: boolean;
  tempId?: string;
}

export interface CreateMessageDto {
  content: MessageContent;
}

class SavV2MessagesApi {
  /**
   * Récupérer les messages internes
   */
  static async getInternalMessages(
    savId: number,
    page = 0,
    total = 40
  ): Promise<IListResult<Message>> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/messages/internal?page=${page}&total=${total}`
    );
    return response.data;
  }

  /**
   * Récupérer les messages du bénéficiaire
   */
  static async getBeneficiaryMessages(
    savId: number,
    page = 0,
    total = 40
  ): Promise<IListResult<Message>> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/messages/beneficiary?page=${page}&total=${total}`
    );
    return response.data;
  }

  /**
   * Envoyer un message interne
   */
  static async sendInternalMessage(
    savId: number,
    content: MessageContent,
    requiresAttention?: boolean,
    reminderDate?: string // Format ISO
  ): Promise<Message> {
    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/messages/internal`,
      { content, requiresAttention, reminderDate }
    );
    return response.data;
  }

  /**
   * Envoyer un message au bénéficiaire
   */
  static async sendBeneficiaryMessage(
    savId: number,
    content: MessageContent,
    changeStateId?: boolean
  ): Promise<Message> {
    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/messages/beneficiary`,
      {
        content,
        ...(changeStateId !== undefined ? { changeStateId } : {})
      }
    );
    return response.data;
  }

  /**
   * Récupérer les messages du garant
   */
  static async getGarantMessages(
    savId: number,
    page = 0,
    total = 40
  ): Promise<IListResult<Message>> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/messages/garant?page=${page}&total=${total}`
    );
    return response.data;
  }

  /**
   * Destinataire effectif des messages garant : contact de la société GARANTE
   * résolue côté API (même logique que l'envoi). À afficher dans les modales
   * d'envoi/relance pour que le gestionnaire sache à qui part l'email.
   */
  static async getGarantRecipient(savId: number): Promise<{
    email: string | null;
    name: string;
    /** Nom du responsable SAV quand l'adresse utilisée est la sienne */
    contactName: string | null;
    source: 'savManagerEmail' | 'savEmail' | 'email' | null;
    companyUuid: string | null;
  }> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/messages/garant-recipient`
    );
    return response.data;
  }

  /**
   * Envoyer un message au garant
   */
  static async sendGarantMessage(
    savId: number,
    content: MessageContent
  ): Promise<Message> {
    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/messages/garant`,
      { content }
    );
    return response.data;
  }

  /**
   * Récupérer un message par son ID
   */
  static async getMessage(messageId: number): Promise<Message> {
    const response = await authenticatedApi.get(
      `/messages/${messageId}`
    );
    return response.data;
  }

  /**
   * Récupérer un message spécifique par ID et channel
   */
  static async getMessageByIdAndChannel(
    savId: number,
    channel: 'internal' | 'beneficiary' | 'garant',
    messageId: number
  ): Promise<Message> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/messages/${channel}/${messageId}`
    );
    return response.data;
  }

  /**
   * Marquer les messages du bénéficiaire comme traités
   */
  static async markBeneficiaryMessagesProcessed(savId: number): Promise<void> {
    await authenticatedApi.post(
      `/sav-v2/${savId}/messages/beneficiary/mark-processed`
    );
  }

  /**
   * Marquer les messages du garant comme traités
   */
  static async markGarantMessagesProcessed(savId: number): Promise<void> {
    await authenticatedApi.post(
      `/sav-v2/${savId}/messages/garant/mark-processed`
    );
  }

  /**
   * Marquer les messages internes comme traités
   */
  static async markInternalMessagesProcessed(savId: number): Promise<void> {
    await authenticatedApi.post(
      `/sav-v2/${savId}/messages/internal/mark-processed`
    );
  }

  /**
   * Marquer les messages comme lus par le bénéficiaire
   */
  static async markMessagesReadByBeneficiary(savId: number): Promise<void> {
    await authenticatedApi.post(
      `/sav-v2/${savId}/messages/beneficiary/mark-read`
    );
  }
}

export default SavV2MessagesApi;